import type { LaunchPadData, PublicCollectionData } from '@mogul/api-client';
import { LaunchPadSort, NftInclude } from '@mogul/api-client';
import type { FC } from 'react';
import { useEffect, useState } from 'react';

import { collectionsApi, launchPadApi } from '../../../api';
import { getNftIfMinted } from '../../../api-helpers/nft';
import { LaunchPadList } from '../../../components/launchpad-list/LaunchPadList.component';
import { SearchBar } from '../../../components/search-bar/SearchBar.component';
import { AVERAGE_BLOCK_TIME_BSC } from '../../../constants/voting';
import { useWeb3State } from '../../../hooks';
import { useMovieVotingContract } from '../../../hooks/movie-voting.hooks';
import { useVotingContract } from '../../../hooks/voting.hooks';
import {
  getCurrentChainKey,
  MOVIE_VOTING_ADDRESS,
  MOVIE_VOTING_NFT_ADDRESS,
  WEB3_CONFIG,
} from '../../../lib';
import styles from './LaunchpadListPage.module.css';

export const LaunchPadListPage: FC = () => {
  const { walletDetails } = useWeb3State();
  const [featuredLaunchpadData, setFeaturedLaunchpadData] = useState<
    LaunchPadData[]
  >([]);
  const [launchpadData, setLaunchpadData] = useState<LaunchPadData[]>([]);
  const [collectionData, setCollectionData] = useState<PublicCollectionData[]>(
    [],
  );

  const movieVotingContract = useMovieVotingContract({
    overrideAddress: MOVIE_VOTING_ADDRESS.MAINNET,
    overrideProvider: 'MAINNET',
    hardOverride: false,
  });

  const votingContract = useVotingContract({
    overrideAddress: WEB3_CONFIG.contracts[getCurrentChainKey()].movieVoting,
    overrideProvider: walletDetails.chain.type,
    hardOverride: false,
  });

  const {
    initialized,
    getMovieVotingRounds,
    getCurrentBlock,
    getBlockTimestamp,
  } =
    walletDetails.chain.id === WEB3_CONFIG.chains.eth.id
      ? movieVotingContract
      : votingContract;

  const [estimatedEndDate, setEstimatedEndDate] = useState(new Date());
  const [isVotingEnded, setIsVotingEnded] = useState<boolean>();
  const [votingPosters, setVotingPosters] = useState<string[]>([]);
  const [roundStarted, setRoundStarted] = useState(false);
  const [roundEnded, setRoundEnded] = useState(false);

  useEffect(() => {
    const getData = async () => {
      const launchpads = await launchPadApi.listLaunchPads({
        limit: 100,
        offset: 0,
        sort: LaunchPadSort.UpdatedAtDesc,
      });

      setLaunchpadData(launchpads.data.data);

      const featuredLaunchpads = await launchPadApi.listLaunchPads({
        limit: 100,
        offset: 0,
        sort: LaunchPadSort.UpdatedAtDesc,
        isFeatured: true,
      });

      setFeaturedLaunchpadData(featuredLaunchpads.data.data);

      const collections = await collectionsApi.listCollections({
        limit: 100,
        offset: 0,
        isFeatured: true,
      });

      setCollectionData(collections.data.data);
    };
    void getData();
  }, [walletDetails.chain.id]);

  //*****************GET MOVIE VOTING INFO************** */
  const setUpVoting = async () => {
    const {
      currentRound: currentRoundsState,
      futureRounds: futureRoundsState,
    } = await getMovieVotingRounds();

    if (!currentRoundsState) {
      return;
    }

    const votingImages: string[] = [];
    for (const movieId of currentRoundsState.movieIds) {
      const nft = await getNftIfMinted(
        MOVIE_VOTING_NFT_ADDRESS[walletDetails.chain.type],
        movieId,
        [NftInclude.Project],
      );

      const movieData = nft?.project;
      const result = {
        description: movieData?.description ?? '',
        genre: movieData?.genre ?? '',
        image: movieData?.posterImage?.publicUrl ?? '',
        name: movieData?.title ?? '',
        studio: movieData?.studio ?? '',
      };
      votingImages.push(result.image);
    }
    setVotingPosters(votingImages);

    const block = await getCurrentBlock();
    const startBlockNumber = parseInt(currentRoundsState.startBlock, 10);
    const endBlockNumber = parseInt(currentRoundsState.endBlock, 10);
    const endBlockDifference = endBlockNumber - block.number;
    const startDifference = startBlockNumber - block.number;
    const endBlockTimestamp = await getBlockTimestamp(endBlockNumber);

    setRoundStarted(block.number > startBlockNumber);
    setRoundEnded(block.number > endBlockNumber);
    if (block.number > endBlockNumber && futureRoundsState.length === 0) {
      setIsVotingEnded(true);
    }

    // 3.01s for average block time (BSC)
    if (startDifference > 0) {
      const endDate = new Date(
        Date.now() + startDifference * 1000 * AVERAGE_BLOCK_TIME_BSC,
      );
      setEstimatedEndDate(endDate);
    } else if (endBlockDifference > 0) {
      const endDate = new Date(
        Date.now() + endBlockDifference * 1000 * AVERAGE_BLOCK_TIME_BSC,
      );
      setEstimatedEndDate(endDate);
    } else {
      const endDate = new Date(endBlockTimestamp * 1000);
      setEstimatedEndDate(endDate);
    }
  };

  useEffect(() => {
    if (!initialized) {
      return;
    }

    void setUpVoting();
  }, [initialized]);

  return (
    <div
      style={{ minHeight: '100vh' }}
      className="flex flex-col items-center bg-container bg-cover bg-repeat"
    >
      <div className="max-w-screen-lg mb-20 mx-0 sm:w-11/12">
        <div className="flex flex-row justify-between items-center">
          <h1 className={`${styles.headers} sm:mt-6 mt-20 mb-0 sm:ml-0 ml-5`}>
            Showcase
          </h1>
        </div>
        <SearchBar tailwindOverride={'sm:mt-2 mb-4'} />
        <div>
          <LaunchPadList launchpads={launchpadData} />
        </div>
      </div>
    </div>
  );
};
